import { alpha } from '@mui/material';

import { palette } from './palette';

type ComponentTokensKeys =
  | 'accordionContentValue'
  | 'accordionHeaderValueActive'
  | 'accordionHeaderValueInactive'
  | 'additionalInfoBackgroundInfo'
  | 'additionalInfoBackgroundSuccess'
  | 'additionalInfoBackgroundWarning'
  | 'additionalInfoBorderInfo'
  | 'additionalInfoBorderSuccess'
  | 'additionalInfoBorderWarning'
  | 'additionalInfoIconInfo'
  | 'additionalInfoIconWarning'
  | 'additionalInfoValueDefault'
  | 'animationBackground'
  | 'bankCardBackgroundDefault'
  | 'bankCardBorderDefault'
  | 'bankCardValueHeader'
  | 'bankCardValueSubHeader'
  | 'bankCardValueSupport'
  | 'bankTileValue'
  | 'bannerBackgroundDefault'
  | 'bannerBorderDefault'
  | 'bodyBackground'
  | 'bottomSheetBackgroundDefault'
  | 'bottomSheetBackgroundSurface'
  | 'bottomSheetBorderDefault'
  | 'bottomSheetValueBody'
  | 'bottomSheetValueHeader'
  | 'branchBankBackgroundDefault'
  | 'buttonContainedBackgroundDefault'
  | 'buttonContainedBackgroundDisabled'
  | 'buttonContainedBackgroundHover'
  | 'buttonContainedBackgroundPressed'
  | 'buttonContainedValueDefault'
  | 'buttonContainedValueDisabled'
  | 'buttonOutlinedBackgroundDefault'
  | 'buttonOutlinedBackgroundDisabled'
  | 'buttonOutlinedBackgroundHover'
  | 'buttonOutlinedBorderDefault'
  | 'buttonOutlinedBorderDisabled'
  | 'buttonOutlinedBorderHover'
  | 'buttonOutlinedValueDefault'
  | 'buttonOutlinedValueDisabled'
  | 'buttonSmallBackgroundDefault'
  | 'buttonSmallBackgroundDisabled'
  | 'buttonSmallBackgroundHover'
  | 'buttonSmallBackgroundPressed'
  | 'buttonSmallValueDefault'
  | 'buttonSmallValueDisabled'
  | 'cardValueHeader'
  | 'cardValueSubHeader'
  | 'controlsBorderActive'
  | 'controlsBorderDefault'
  | 'controlsBorderDisabled'
  | 'controlsBorderHover'
  | 'controlsInnerActive'
  | 'controlsInnerDefault'
  | 'controlsInnerHover'
  | 'controlsValueActive'
  | 'controlsValueDefault'
  | 'controlsValueDisabled'
  | 'iconFunctionalActive'
  | 'iconFunctionalDefault'
  | 'iconFunctionalDisabled'
  | 'iconFunctionalWhite'
  | 'iconHoldingIconBackground'
  | 'iconHoldingIconIcon'
  | 'iconOnboardingDefault'
  | 'iconOnboardingHighlight'
  | 'iconPrimary'
  | 'iconSecondary'
  | 'inputBackgroundDefault'
  | 'inputBackgroundDisabled'
  | 'inputBorderActive'
  | 'inputBorderDefault'
  | 'inputBorderError'
  | 'inputBorderHover'
  | 'inputValueActive'
  | 'inputValueDefault'
  | 'inputValueHelpDefault'
  | 'inputValueHelpError'
  | 'inputValueHelpLink'
  | 'inputValueHelpSuccess'
  | 'loadingOverlayText'
  | 'lottieAnimationStroke'
  | 'navigationHeader'
  | 'onboardingValueBody'
  | 'onboardingValueHeader'
  | 'optionCardsBackgroundDefault'
  | 'optionCardsBackgroundHover'
  | 'optionCardsBorderActive'
  | 'optionCardsBorderDefault'
  | 'optionCardsBorderHover'
  | 'optionCardsValueBody'
  | 'optionCardsValueDisabled'
  | 'optionCardsValueHeader'
  | 'progressBarsBackground'
  | 'progressBarsLoader'
  | 'searchBackgroundDefault'
  | 'searchBackgroundDisabled'
  | 'searchBackgroundHover'
  | 'searchBackgroundPressed'
  | 'searchBackgroundResultsDefault'
  | 'searchBackgroundResultsHover'
  | 'searchBackgroundResultsPressed'
  | 'searchBorderActive'
  | 'searchBorderDefault'
  | 'searchBorderHover'
  | 'searchValueActive'
  | 'searchValueDefault'
  | 'searchValueRowDefault'
  | 'searchValueRowHover'
  | 'segmentedControlBackgroundActive'
  | 'segmentedControlBackgroundActiveFocused'
  | 'segmentedControlBackgroundBackground'
  | 'segmentedControlBackgroundSurface'
  | 'segmentedControlBorderDefault'
  | 'segmentedControlValueDefault'
  | 'statusValueBody'
  | 'statusValueHeader'
  | 'summaryBorder'
  | 'summaryValue'
  | 'tagBackground'
  | 'tagValue'
  | 'textLinksInlineTextLinksBackground'
  | 'textLinksInlineTextLinksBackgroundBorder'
  | 'textLinksInlineTextLinksKeyline'
  | 'textLinksInlineTextLinksText'
  | 'textLinksTextButtonsDefault'
  | 'textLinksTextButtonsDisabled'
  | 'textLinksTextButtonsHover'
  | 'textLinksTextButtonsPressed'
  | 'toastsErrorBackground'
  | 'toastsInfoBackground'
  | 'toastsText'
  | 'toggleActiveBackground'
  | 'toggleActiveDisabledBackground'
  | 'toggleActiveDisabledColor'
  | 'toggleBorderFocused'
  | 'toggleDefaultColor'
  | 'toggleInactiveBackground'
  | 'toggleInactiveDisabledBackground'
  | 'toggleInactiveDisabledColor'
  | 'toggleInfoText'
  | 'tooltipsBackground'
  | 'tooltipsText';

type Color = string;
type Tokens = Record<ComponentTokensKeys, Color>;

declare module '@mui/material/styles' {
  interface Theme {
    tokens: Tokens;
    coreTokens: CoreTokens;
    voltBranding: {
      poweredBy: boolean;
      appLogo: boolean;
    };
    logos?: Logos;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    tokens: Tokens;
    coreTokens: CoreTokens;
    voltBranding: {
      poweredBy: boolean;
      appLogo: boolean;
    };
    logos?: Logos;
  }
}

export type Logos = {
  app?: string;
};

export type CoreTokens = {
  primary: {
    white: Color;
    translucent: Color;
    hint: Color;
    soft: Color;
    light: Color;
    hazy: Color;
    muted: Color;
    balanced: Color;
    main: Color;
    deep: Color;
  };
  secondary: {
    white: Color;
    translucent: Color;
    hint: Color;
    soft: Color;
    light: Color;
    hazy: Color;
    muted: Color;
    balanced: Color;
    main: Color;
    deep: Color;
  };
  text: {
    white: Color;
    faint: Color;
    muted: Color;
    balanced: Color;
    main: Color;
    dark: Color;
    hazy: Color;
  };
  border: {
    soft: Color;
    softBlue: Color;
    light: Color;
    faint: Color;
    muted: Color;
    main: Color;
    active: Color;
  };
  background: {
    translucent: Color;
    hint: Color;
    soft: Color;
    light: Color;
    muted: Color;
    main: Color;
    faint: Color;
    success: Color;
  };
  states: {
    hintInfo: Color;
    hintError: Color;
    softInfo: Color;
    error: Color;
    default: Color;
    disabled: Color;
    hover: Color;
    success: Color;
  };
};

export const getComponentTokens = (
  core: CoreTokens
): Record<ComponentTokensKeys, Color> => ({
  accordionHeaderValueActive: core.states.default,
  accordionHeaderValueInactive: core.text.main,
  accordionContentValue: core.primary.balanced,
  additionalInfoBackgroundInfo: core.states.hintInfo,
  additionalInfoBackgroundSuccess: core.background.success,
  additionalInfoBackgroundWarning: core.states.hintError,
  additionalInfoBorderInfo: core.states.softInfo,
  additionalInfoBorderSuccess: core.states.softInfo,
  additionalInfoBorderWarning: core.states.softInfo,
  additionalInfoIconInfo: core.states.default,
  additionalInfoIconWarning: core.states.error,
  additionalInfoValueDefault: core.text.main,
  animationBackground: core.background.faint,
  bankCardBackgroundDefault: core.primary.white,
  bankCardBorderDefault: core.border.softBlue,
  bankCardValueHeader: core.text.main,
  bankCardValueSubHeader: core.text.muted,
  bankCardValueSupport: core.text.faint,
  bankTileValue: core.text.main,
  bottomSheetBackgroundDefault: core.primary.white,
  bottomSheetBackgroundSurface: core.secondary.hint,
  bodyBackground: core.background.soft,
  bottomSheetBorderDefault: core.border.softBlue,
  bottomSheetValueBody: core.text.muted,
  bottomSheetValueHeader: core.text.main,
  branchBankBackgroundDefault: core.background.translucent,
  buttonContainedBackgroundDefault: core.primary.main,
  buttonContainedBackgroundDisabled: core.background.soft,
  buttonContainedBackgroundHover: core.primary.deep,
  buttonContainedBackgroundPressed: core.primary.balanced,
  buttonContainedValueDefault: core.text.white,
  buttonContainedValueDisabled: core.text.faint,
  buttonOutlinedBackgroundDefault: core.primary.white,
  buttonOutlinedBackgroundDisabled: core.background.soft,
  buttonOutlinedBackgroundHover: core.primary.soft,
  buttonOutlinedBorderDefault: core.primary.light,
  buttonOutlinedBorderDisabled: core.background.light,
  buttonOutlinedBorderHover: core.primary.muted,
  buttonOutlinedValueDefault: core.text.main,
  buttonOutlinedValueDisabled: core.text.faint,
  buttonSmallBackgroundDefault: core.primary.soft,
  buttonSmallBackgroundDisabled: core.primary.soft,
  buttonSmallBackgroundHover: core.primary.hazy,
  buttonSmallBackgroundPressed: core.primary.light,
  buttonSmallValueDefault: core.text.main,
  buttonSmallValueDisabled: core.text.faint,
  cardValueHeader: core.text.main,
  cardValueSubHeader: core.text.muted,
  controlsBorderActive: core.border.main,
  controlsBorderDefault: core.border.faint,
  controlsBorderDisabled: core.border.faint,
  controlsBorderHover: core.border.faint,
  controlsInnerActive: core.primary.main,
  controlsInnerDefault: core.primary.white,
  controlsInnerHover: core.border.soft,
  controlsValueActive: core.text.main,
  controlsValueDefault: core.text.muted,
  controlsValueDisabled: core.text.hazy,
  iconPrimary: core.secondary.main,
  iconSecondary: core.secondary.light,
  iconFunctionalActive: core.text.main,
  iconFunctionalDefault: core.text.muted,
  iconFunctionalDisabled: core.text.faint,
  iconFunctionalWhite: core.text.white,
  iconHoldingIconBackground: core.background.hint,
  iconHoldingIconIcon: core.primary.hazy,
  iconOnboardingDefault: core.text.main,
  iconOnboardingHighlight: core.text.faint,
  inputBackgroundDefault: core.background.translucent,
  inputBackgroundDisabled: core.background.soft,
  inputBorderActive: core.border.active,
  inputBorderDefault: core.background.light,
  inputBorderError: core.states.error,
  inputBorderHover: core.background.muted,
  inputValueActive: core.text.main,
  inputValueDefault: core.text.faint,
  inputValueHelpDefault: core.text.muted,
  inputValueHelpError: core.states.error,
  inputValueHelpLink: core.states.default,
  inputValueHelpSuccess: core.states.success,
  lottieAnimationStroke: core.border.active,
  navigationHeader: core.text.main,
  onboardingValueBody: core.text.muted,
  onboardingValueHeader: core.text.main,
  optionCardsBackgroundDefault: core.primary.white,
  optionCardsBackgroundHover: core.primary.translucent,
  optionCardsBorderActive: core.border.active,
  optionCardsBorderDefault: core.border.softBlue,
  optionCardsBorderHover: core.border.faint,
  optionCardsValueBody: core.text.muted,
  optionCardsValueDisabled: core.text.faint,
  optionCardsValueHeader: core.text.main,
  progressBarsBackground: core.secondary.soft,
  progressBarsLoader: core.secondary.main,
  searchBackgroundDefault: core.primary.translucent,
  searchBackgroundDisabled: core.primary.soft,
  searchBackgroundHover: core.primary.translucent,
  searchBackgroundPressed: core.primary.translucent,
  searchBackgroundResultsDefault: core.primary.white,
  searchBackgroundResultsHover: core.primary.hint,
  searchBackgroundResultsPressed: core.primary.translucent,
  searchBorderActive: core.border.active,
  searchBorderDefault: core.border.light,
  bannerBorderDefault: core.border.light,
  bannerBackgroundDefault: alpha(core.background.main, 0.03),
  searchBorderHover: core.border.muted,
  searchValueActive: core.text.main,
  searchValueDefault: core.text.faint,
  searchValueRowDefault: core.text.balanced,
  searchValueRowHover: core.text.main,
  segmentedControlBackgroundBackground: core.primary.hint,
  segmentedControlBackgroundActive: core.primary.soft,
  segmentedControlBackgroundActiveFocused: core.primary.light,
  segmentedControlBackgroundSurface: core.primary.white,
  segmentedControlBorderDefault: core.primary.soft,
  segmentedControlValueDefault: core.text.main,
  statusValueBody: core.text.muted,
  statusValueHeader: core.text.main,
  summaryBorder: core.states.softInfo,
  summaryValue: core.text.main,
  tagBackground: core.states.softInfo,
  tagValue: core.states.default,
  textLinksInlineTextLinksBackgroundBorder: core.secondary.soft,
  textLinksInlineTextLinksBackground: core.secondary.translucent,
  textLinksInlineTextLinksKeyline: core.primary.soft,
  textLinksInlineTextLinksText: core.text.main,
  textLinksTextButtonsDefault: core.border.active,
  textLinksTextButtonsDisabled: core.background.soft,
  textLinksTextButtonsHover: core.primary.deep,
  textLinksTextButtonsPressed: core.primary.muted,
  toastsInfoBackground: core.primary.muted,
  toastsErrorBackground: core.states.error,
  toastsText: core.text.white,
  toggleActiveBackground: core.states.default,
  toggleActiveDisabledColor: core.primary.white,
  toggleActiveDisabledBackground: core.states.default,
  toggleDefaultColor: core.primary.white,
  toggleBorderFocused: core.border.active,
  toggleInactiveBackground: core.background.light,
  toggleInactiveDisabledColor: core.primary.white,
  toggleInactiveDisabledBackground: core.background.light,
  toggleInfoText: core.text.muted,
  tooltipsBackground: core.primary.main,
  tooltipsText: core.text.white,
  loadingOverlayText: core.text.white,
});

export const voltCore: CoreTokens = {
  primary: {
    white: palette.white,
    translucent: palette.navy10,
    hint: palette.navy25,
    soft: palette.navy50,
    light: palette.navy75,
    hazy: palette.navy100,
    muted: palette.navy300,
    balanced: palette.navy400,
    main: palette.navy500,
    deep: palette.navy700,
  },
  secondary: {
    white: palette.white,
    translucent: palette.grey10,
    hint: palette.grey25,
    soft: palette.grey50,
    light: palette.grey75,
    hazy: palette.grey100,
    muted: palette.grey300,
    balanced: palette.grey400,
    main: palette.grey500,
    deep: palette.grey700,
  },
  text: {
    white: palette.white,
    hazy: palette.steel100,
    faint: palette.steel200,
    muted: palette.steel300,
    balanced: palette.steel400,
    main: palette.steel500,
    dark: palette.steel900,
  },
  background: {
    translucent: palette.navy10,
    hint: palette.navy25,
    soft: palette.navy50,
    light: palette.navy75,
    muted: palette.navy300,
    main: palette.navy500,
    faint: palette.blue10,
    success: palette.green50,
  },
  border: {
    soft: palette.navy50,
    softBlue: palette.blue50,
    light: palette.navy75,
    faint: palette.navy200,
    muted: palette.navy300,
    main: palette.navy500,
    active: palette.blue500,
  },
  states: {
    hintInfo: palette.blue25,
    hintError: palette.red10,
    softInfo: palette.blue50,
    error: palette.red500,
    default: palette.blue500,
    disabled: palette.blue300,
    hover: palette.blue600,
    success: palette.green700,
  },
};

export const unbrandedCore: CoreTokens = {
  primary: {
    white: palette.white,
    translucent: palette.grey10,
    hint: palette.grey25,
    soft: palette.grey50,
    light: palette.grey75,
    hazy: palette.grey100,
    muted: palette.grey300,
    balanced: palette.grey400,
    main: palette.grey500,
    deep: palette.grey700,
  },
  secondary: {
    white: palette.white,
    translucent: palette.grey10,
    hint: palette.grey25,
    soft: palette.grey50,
    light: palette.grey75,
    hazy: palette.grey100,
    muted: palette.grey300,
    balanced: palette.grey400,
    main: palette.grey500,
    deep: palette.grey700,
  },
  text: {
    white: palette.white,
    hazy: palette.grey300,
    faint: palette.grey400,
    muted: palette.grey500,
    balanced: palette.grey600,
    main: palette.grey700,
    dark: palette.grey900,
  },
  border: {
    soft: palette.grey50,
    softBlue: palette.grey50,
    light: palette.grey75,
    faint: palette.grey200,
    muted: palette.grey300,
    main: palette.grey500,
    active: palette.blue500,
  },
  background: {
    translucent: palette.navy10,
    hint: palette.navy25,
    soft: palette.navy50,
    light: palette.navy75,
    muted: palette.navy300,
    main: palette.navy500,
    faint: palette.blue10,
    success: palette.green50,
  },
  states: {
    hintInfo: palette.blue25,
    hintError: palette.red10,
    softInfo: palette.blue50,
    error: palette.red500,
    default: palette.blue500,
    disabled: palette.blue300,
    hover: palette.blue600,
    success: palette.green700,
  },
};
